import type { Earthquake } from '@/types';
import { API_ENDPOINTS } from '@/config/data-sources';

// ---------------------------------------------------------------------------
// USGS GeoJSON Earthquake Feed
// https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_day.geojson
// ---------------------------------------------------------------------------

const TIMEOUT_MS = 15_000;

interface USGSFeature {
  id: string;
  properties: {
    mag: number;
    place: string | null;
    time: number;
    url: string;
    tsunami: number;
    felt: number | null;
    alert: string | null;
    status: string;
    type: string;
    title: string;
  };
  geometry: {
    type: string;
    coordinates: [number, number, number]; // [lon, lat, depth]
  };
}

interface USGSResponse {
  type: string;
  metadata: {
    generated: number;
    url: string;
    title: string;
    count: number;
  };
  features: USGSFeature[];
}

/**
 * Fetch M4.5+ earthquakes from the past 24 hours via USGS GeoJSON API.
 * Falls back to the significant-week feed if the primary endpoint fails.
 */
export async function fetchEarthquakes(): Promise<Earthquake[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(API_ENDPOINTS.USGS_EARTHQUAKES, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`USGS API responded with ${res.status}`);
    }

    const data: USGSResponse = await res.json();
    return parseFeatures(data.features);
  } catch (err) {
    console.warn('[USGS] Primary feed failed, trying significant-week fallback', err);
    return fetchFallback();
  } finally {
    clearTimeout(timer);
  }
}

async function fetchFallback(): Promise<Earthquake[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(API_ENDPOINTS.USGS_SIGNIFICANT, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`USGS fallback responded with ${res.status}`);
    }

    const data: USGSResponse = await res.json();
    return parseFeatures(data.features);
  } catch (err) {
    console.error('[USGS] Fallback also failed', err);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

function parseFeatures(features: USGSFeature[]): Earthquake[] {
  if (!Array.isArray(features)) return [];

  return features
    .filter((f) => f.geometry?.coordinates && f.properties?.mag != null)
    .map((f): Earthquake => {
      const [lon, lat, depth] = f.geometry.coordinates;
      const p = f.properties;

      return {
        id: f.id ?? `usgs-${p.time}`,
        magnitude: p.mag,
        place: p.place ?? 'Unknown location',
        lat,
        lon,
        depth: Math.round(depth * 10) / 10,
        timestamp: new Date(p.time).toISOString(),
        url: p.url ?? `https://earthquake.usgs.gov/earthquakes/eventpage/${f.id}`,
        tsunami: p.tsunami === 1,
        felt: p.felt ?? undefined,
      };
    })
    .sort((a, b) => b.magnitude - a.magnitude);
}
